// A daily history chart in a panel: one class's value
// (/v1/classes/{class}/history), the all-class headline (/v1/classes/history)
// or SOFR (/v1/benchmarks/sofr). A year is read once; the chart's presets and
// brush pick the window from it. Estimated (backfill) days say so in the
// tooltip, net flows sit in a bar strip under the line, and a series shorter
// than a year says when history starts. Sparse while snapshots build up.

import { useEffect, useMemo, useState } from "react";

import { NotPublished } from "../api";
import { changeNoteText, dailySeries, fetchClassHistoryAll, fetchIndexHistoryAll, fetchSofr, historyDate, sofrSeries, type DailySeries } from "../class-history";
import { dailyFlows } from "../flows";
import { compactUsd } from "../format";
import { axisRate } from "../stablecoins-chart";
import styles from "./Stablecoins.module.css";
import { ValueChart, type PointDetail } from "./ValueChart";

/** usd: dollar values, change in percent. rate: fractions shown as percent, change in points. */
export type ChartUnit = "usd" | "rate";

/** A year: the longest preset. */
const DAYS = 365;

type Loaded = { series: DailySeries; changeNote: string | null; flows: Array<[number, number | null]> };
type Load = { kind: "loading" } | { kind: "pending" } | { kind: "error"; message: string } | ({ kind: "ready" } & Loaded);

/** What to chart: one class by its /v1/classes id, the headline, or SOFR. */
export type HistorySubject = { kind: "class"; classApiId: string } | { kind: "headline" } | { kind: "sofr" };

function loadSeries(subject: HistorySubject, signal: AbortSignal): Promise<Loaded> {
  if (subject.kind === "sofr") return fetchSofr(DAYS, signal).then((d) => ({ series: sofrSeries(d), changeNote: null, flows: [] }));
  return subject.kind === "class"
    ? fetchClassHistoryAll(subject.classApiId, signal).then((h) => ({
        series: dailySeries(h.points, (p) => p.value_usd),
        changeNote: changeNoteText(h.change_note),
        flows: dailyFlows(h.points),
      }))
    : fetchIndexHistoryAll(signal).then((h) => ({
        series: dailySeries(h.points, (p) => p.total_value_usd),
        changeNote: changeNoteText(h.change_note),
        flows: dailyFlows(h.points),
      }));
}

/** Per-day tooltip extras: estimated (backfill) days, entries counted, coverage events. */
function pointDetails(series: DailySeries): Map<number, PointDetail> {
  const map = new Map<number, PointDetail>();
  series.points.forEach(([t], i) => {
    const d = series.details[i];
    map.set(t, {
      estimated: series.estimatedThrough !== null && t <= series.estimatedThrough,
      entries: d?.entries ?? null,
      events: d?.events ?? [],
      coverageUsd: d?.coverageUsd ?? null,
      unconfirmed: d?.unconfirmed ?? false,
      estimate: d?.estimate ?? null,
      weekly: d?.weekly ?? false,
    });
  });
  return map;
}

export function HistoryPanel({
  subject,
  title,
  chartTitle,
  unit = "usd",
  footnote,
}: {
  subject: HistorySubject;
  title: string;
  chartTitle: string;
  unit?: ChartUnit;
  /** Shown under the chart, after the history notes (e.g. the source). */
  footnote?: string;
}) {
  const [load, setLoad] = useState<Load>({ kind: "loading" });
  const key = subject.kind === "class" ? subject.classApiId : subject.kind;

  useEffect(() => {
    const controller = new AbortController();
    setLoad({ kind: "loading" });
    loadSeries(subject, controller.signal)
      .then((loaded) => setLoad({ kind: "ready", ...loaded }))
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setLoad(e instanceof NotPublished ? { kind: "pending" } : { kind: "error", message: e instanceof Error ? e.message : String(e) });
      });
    return () => controller.abort();
    // `key` stands for the subject, which callers rebuild on every render.
  }, [key]);

  const series = load.kind === "ready" ? load.series : null;
  const details = useMemo(() => (series ? pointDetails(series) : undefined), [series]);
  // SOFR is published on business days; only Panther's own history can be short.
  const span = series && series.points.length ? series.points[series.points.length - 1]![0] - series.points[0]![0] : 0;
  const short = subject.kind !== "sofr" && series?.from && span < (DAYS - 1) * 86_400_000;
  const notes = [short ? `history starts ${historyDate(series!.from!)}` : null, footnote ?? null].filter((n): n is string => n !== null);

  return (
    <section aria-label={title} className={`panel ${styles.panel}`}>
      <header className="panel-head">
        <h2>{title}</h2>
      </header>
      {series ? (
        <>
          <ValueChart
            changeKind={unit === "rate" ? "pt" : "pct"}
            defaultPreset={subject.kind === "sofr" ? "90d" : "all"}
            details={details}
            exportName={chartTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}
            flows={load.kind === "ready" && load.flows.length ? load.flows : undefined}
            format={unit === "rate" ? axisRate : compactUsd}
            // Panther's own history (class and headline) has coverage events: its change is like for like.
            likeForLike={subject.kind !== "sofr"}
            points={series.points}
            title={chartTitle}
          />
          {notes.length ? <p className={styles.historyNote}>{notes.join(" · ")}</p> : null}
          {load.kind === "ready" && load.changeNote ? <p className={styles.historyNote}>{load.changeNote}</p> : null}
        </>
      ) : (
        <p className={styles.historyNote}>
          {load.kind === "loading" ? "Reading history…" : load.kind === "pending" ? "No history published yet." : load.kind === "error" ? `History unavailable right now: ${load.message}` : null}
        </p>
      )}
    </section>
  );
}
